import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import Logo from "./brand/Logo";
import {
  EMAIL_ADDRESS,
  EMAIL_HREF,
  PHONE_DISPLAY,
  PHONE_HREF,
  EXTERNAL_LINK_PROPS,
} from "../config/contact";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative mt-24 sm:mt-32 border-t border-white/5">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Logo variant="full" height={92} testId="footer-logo" />
            <p className="mt-7 max-w-md text-[15px] leading-[1.7] text-mute-200">
              Linked2Marketing — digital systems and marketing infrastructure for premium
              service brands. We design the invisible layer that supports exceptional guest and
              customer experiences.
            </p>
            <Link
              to="/#enquiry"
              data-testid="footer-cta-book-call"
              className="mt-7 inline-flex btn-primary"
            >
              Start a conversation <ArrowUpRight className="w-4 h-4" />
            </Link>

            <div className="mt-6 flex flex-col gap-2 text-[13.5px] text-mute-200">
              <a
                href={EMAIL_HREF}
                data-testid="footer-email"
                className="inline-flex items-center gap-2 hover:text-gold transition-colors"
              >
                <Mail className="w-3.5 h-3.5 text-mute-300" />
                {EMAIL_ADDRESS}
              </a>
              <a
                href={PHONE_HREF}
                data-testid="footer-phone"
                className="inline-flex items-center gap-2 hover:text-gold transition-colors tabular-nums"
              >
                <Phone className="w-3.5 h-3.5 text-mute-300" />
                {PHONE_DISPLAY}
              </a>
            </div>

            <p className="mt-6 text-xs text-mute-300">
              UK-based · Strategic digital partner
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <FooterColumn
              title="Studio"
              links={[
                { to: "/#approach", label: "Approach" },
                { to: "/#systems", label: "Systems" },
                { to: "/#sectors", label: "Sectors" },
                { to: "/#proof", label: "Proof" },
                { to: "/insights", label: "Insights" },
              ]}
            />
            <FooterColumn
              title="Sectors"
              links={[
                { to: "/#sectors", label: "Yacht charter" },
                { to: "/#sectors", label: "Luxury travel" },
                { to: "/#sectors", label: "Boutique hospitality" },
                { to: "/#sectors", label: "Tourism operators" },
                { to: "/#sectors", label: "Private experiences" },
                { to: "/#sectors", label: "Concierge services" },
              ]}
            />
            <FooterColumn
              title="Contact"
              links={[
                { to: "/#enquiry", label: "Send an enquiry" },
                { to: "/#enquiry", label: "Book a consultation" },
                { to: "/privacy", label: "Privacy policy" },
                { to: "/cookies", label: "Cookie policy" },
                { to: "/terms", label: "Terms" },
              ]}
            />
          </div>
        </div>

        <div className="hairline mt-14 mb-6" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-mute-300">
          <span>© {new Date().getFullYear()} Linked2Marketing. All rights reserved.</span>
          <span data-testid="footer-credit" className="text-mute-200">
            Created by Linked2Marketing
          </span>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.18em] text-mute-300 font-medium">
        {title}
      </h4>
      <ul className="mt-4 space-y-3">
        {links.map((l) => {
          const testId = `footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
          const cls = "text-sm text-mute-200 hover:text-white transition-colors";
          if (l.external) {
            return (
              <li key={`${title}-${l.label}`}>
                <a
                  href={l.href}
                  {...EXTERNAL_LINK_PROPS}
                  data-testid={testId}
                  className={cls}
                >
                  {l.label}
                </a>
              </li>
            );
          }
          return (
            <li key={`${title}-${l.label}`}>
              <Link to={l.to} data-testid={testId} className={cls}>
                {l.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
